import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Wallet, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Premium warning dialog shown for:
 *  - type="min":          bet amount below minimum
 *  - type="insufficient": wallet balance < total bet
 *  - type="empty":        no bets selected
 *  - type="closed":       market not active
 *  - type="info":         generic
 */
export default function BetWarningModal({ open, type = "info", title, message, hint, onClose }) {
  if (!open) return null;

  const palette = type === "insufficient"
    ? { ring: "border-amber-400/60", glow: "shadow-[0_0_60px_rgba(251,191,36,0.25)]", icon: "text-amber-300", iconBg: "bg-amber-400/15 border-amber-400/40", heading: "text-amber-200" }
    : type === "min"
    ? { ring: "border-cyan-400/50", glow: "shadow-[0_0_60px_rgba(56,189,248,0.25)]", icon: "text-cyan-300", iconBg: "bg-cyan-400/10 border-cyan-400/40", heading: "text-cyan-200" }
    : { ring: "border-rose-400/60", glow: "shadow-[0_0_60px_rgba(244,63,94,0.25)]", icon: "text-rose-300", iconBg: "bg-rose-400/15 border-rose-400/40", heading: "text-rose-200" };

  const Icon = type === "insufficient" ? Wallet : AlertTriangle;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(10,24,41,0.95) 0%, rgba(4,8,18,0.98) 100%)" }}
      onClick={onClose}
      data-testid="bet-warning-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-sm rounded-2xl border ${palette.ring} ${palette.glow}`}
        style={{ background: "linear-gradient(180deg,#0a1829 0%,#060e1b 100%)" }}
        data-testid="bet-warning-modal"
      >
        <button
          onClick={onClose}
          data-testid="bet-warning-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-black/40 border border-slate-700 text-slate-300 flex items-center justify-center active:scale-95"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="pt-7 px-6 text-center">
          <div className={`mx-auto w-16 h-16 rounded-2xl border ${palette.iconBg} flex items-center justify-center mb-4`}>
            <Icon className={`w-8 h-8 ${palette.icon}`} strokeWidth={2.2} />
          </div>
          <h3 className={`font-display font-black text-xl tracking-wide ${palette.heading}`} data-testid="bet-warning-title">
            {title}
          </h3>
          {message && (
            <p className="mt-2 text-slate-300 text-sm leading-relaxed" data-testid="bet-warning-message">
              {message}
            </p>
          )}
          {hint && (
            <p className="mt-1 text-slate-500 text-xs" data-testid="bet-warning-hint">
              {hint}
            </p>
          )}
        </div>

        <div className="p-5 pt-6 grid gap-2">
          {type === "insufficient" ? (
            <>
              <Link
                to="/deposit"
                onClick={onClose}
                data-testid="bet-warning-deposit"
                className="h-12 rounded-xl font-black text-white tracking-wide active:scale-95 inline-flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(56,189,248,0.7)]"
                style={{ background: "linear-gradient(135deg,#22d3ee 0%,#2563eb 100%)" }}
              >
                पैसे जोड़ें
              </Link>
              <button
                onClick={onClose}
                data-testid="bet-warning-ok"
                className="h-11 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 font-bold active:scale-95"
              >
                बंद करें
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              data-testid="bet-warning-ok"
              className="h-12 rounded-xl font-black text-white tracking-wide active:scale-95 shadow-[0_10px_30px_-10px_rgba(56,189,248,0.7)]"
              style={{ background: "linear-gradient(135deg,#22d3ee 0%,#2563eb 100%)" }}
            >
              ठीक है
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
